import { CheckIcon } from "@heroicons/react/outline";
import { capitalize, values } from "lodash";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useBillingPlans } from "../../context/billingPlans/BillingPlansProvider";
import { useMembers } from "../../context/members";
import classNames from "../../utils/classNames";
import { Plan } from "../../utils/types";

interface Props {
  plan: Plan;
  onClickToShowUpgrade?: () => void;
  upgradeText?: string;
  isMobile: boolean;
  className?: string;
  style?: React.CSSProperties;
  showSelectedPlan?: boolean;
  showActionButton?: boolean;
  showFeaturesLink?: boolean;
  featuresWithAsterisk?: boolean;
  withHover?: boolean;
}

const PlanCard: React.FC<Props> = ({
  plan,
  onClickToShowUpgrade,
  isMobile,
  className,
  style,
  upgradeText,
  showSelectedPlan = true,
  showActionButton = true,
  showFeaturesLink = true,
  withHover = true
}) => {
  const { currentPlan, setNewPlan, newPlan } = useBillingPlans();
  const { isCurrentUserAdmin } = useMembers();

  const isCurrentPlan = useMemo(
    () => !!currentPlan && currentPlan?.uuid === plan.uuid,
    [currentPlan, plan.uuid]
  );

  const isNewPlan = useMemo(
    () => newPlan?.plan?.uuid === plan.uuid,
    [newPlan?.plan?.uuid, plan.uuid]
  );

  return (
    <div
      style={style}
      className={classNames(
        className,
        isMobile ? "horizontal-snap-scroll-child mx-0.5" : "mx-2",
        "flex flex-col justify-between relative w-3/12 h-full rounded-lg shadow-md border first:ml-0 last:mr-0 bg-white dark:bg-gray-800 dark:border-gray-500",
        isCurrentPlan &&
          "border-yellow-400 dark:border-yellow-400 cursor-default dark:hover:border-yellow-400",
        isNewPlan &&
          !isCurrentPlan &&
          showSelectedPlan &&
          "border-hotspotty-400 dark:border-white",
        withHover &&
          "hover:border-hotspotty-400 dark:hover:border-white cursor-pointer"
      )}
      onClick={() => {
        setNewPlan((prevState) => ({ ...prevState, plan }));
      }}
    >
      {isCurrentPlan && (
        <span className="flex items-center justify-center absolute w-20 text-xs -mt-2 right-0 left-0 mx-auto uppercase text-white dark:text-black px-2 rounded-full bg-yellow-400">
          Current
        </span>
      )}
      {isNewPlan && !isCurrentPlan && showSelectedPlan && (
        <span className="flex items-center justify-center absolute w-20 text-xs -mt-2 right-0 left-0 mx-auto uppercase text-white dark:text-black px-2 rounded-full bg-hotspotty-400 dark:bg-white">
          Selected
        </span>
      )}
      <div className="flex flex-col items-center justify-center py-6">
        <span className="font-semibold text-3xl capitalize">{plan.name}</span>
        <div className="flex items-center h-20">
          <span className="text-2xl mr-1">$</span>
          <span className="text-5xl font-bold">{(plan.price || 0) / 100}</span>
          <span className="text-sm font-light dark:text-gray-300 ml-1">
            /month
          </span>
        </div>
        {plan?.description && (
          <span className="block px-2 text-sm font-light text-center dark:text-gray-200">
            {capitalize(plan.description)}
          </span>
        )}
      </div>
      <div className="flex flex-col flex-1 justify-between w-full py-4 px-5 border-t dark:border-gray-500">
        <div>
          {values(plan.features).map((item, index) => {
            if (index === 0) {
              return (
                item && (
                  <div
                    key={item}
                    className="text-xs font-bold text-gray-800 dark:text-gray-300 py-2"
                  >
                    {item}
                  </div>
                )
              );
            }

            return (
              <div
                key={item}
                className="flex break-all items-start text-left text-xs font-medium text-gray-800 dark:text-gray-300 py-2"
              >
                <CheckIcon className="w-4 flex-shrink-0 text-green-400 mr-2" />
                {item}
              </div>
            );
          })}
        </div>

        {showFeaturesLink && (
          <Link
            to="/pricing/compare-feature-plans"
            onClick={() => {
              setNewPlan((prevState) => ({ ...prevState, plan }));
            }}
            className="text-xs cursor-pointer hover:underline text-center mt-8"
          >
            Compare feature plans
          </Link>
        )}

        {(!isNewPlan || !showSelectedPlan) &&
          !isCurrentPlan &&
          isCurrentUserAdmin &&
          showActionButton && (
            <div className="flex flex-col items-center mb-2">
              {showActionButton && onClickToShowUpgrade && (
                <button
                  onClick={() => {
                    setNewPlan((prevState) => ({ ...prevState, plan }));
                    onClickToShowUpgrade();
                  }}
                  className="w-full text-xs text-white py-2 mt-4 shadow rounded-md outline-none transition-all bg-hotspotty-400 border-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none"
                >
                  {upgradeText ? upgradeText : "Select this plan"}
                </button>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default PlanCard;
